/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import {
  downloadBinaryFile,
  downloadCsvFile,
  generateFileName,
} from '../../helper/downloadHelper';
import {
  conversationRowsForExport,
  downloadXlsxFile,
  fetchAllConversationsList,
} from '../../helper/reportExportHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const latestSummaryRequest = {
  account: 0,
  bot: 0,
};
let latestConversationsListRequest = 0;

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    resolved_conversations_count: 0,
    conversations_with_incoming_messages_count: 0,
    resolved_conversations_with_incoming_messages_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  summaryUIFlags: {
    isFetchingAccountSummary: false,
    isFetchingBotSummary: false,
  },
  summaryPendingRequests: {
    account: 0,
    bot: 0,
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
      isFetchingConversationsList: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
    agentConversationMetricTotal: 0,
  },
  conversationsList: {
    conversations: [],
    currentPage: 1,
    totalCount: 0,
  },
  agentsInboxesReport: {
    data: {
      agents: [],
      inboxes: [],
      rows: [],
    },
    uiFlags: {
      isFetching: false,
    },
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getReportSummaryUIFlags(_state) {
    return _state.summaryUIFlags;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getAgentConversationMetricTotal(_state) {
    return _state.overview.agentConversationMetricTotal;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getConversationsList(_state) {
    return _state.conversationsList.conversations;
  },
  getConversationsListCurrentPage(_state) {
    return _state.conversationsList.currentPage;
  },
  getConversationsListTotal(_state) {
    return _state.conversationsList.totalCount;
  },
  getAgentsInboxesReport(_state) {
    return _state.agentsInboxesReport.data;
  },
  getAgentsInboxesReportUIFlags(_state) {
    return _state.agentsInboxesReport.uiFlags;
  },
};

const downloadReport = (reportObj, reportType, fetchReport) =>
  fetchReport(reportObj)
    .then(response => {
      downloadCsvFile(reportObj.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType,
        businessHours: reportObj?.businessHours,
        conversationDirection: reportObj?.conversationDirection,
      });
    })
    .catch(error => {
      console.error(error);
    });

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  async fetchConversationsList({ commit }, reportObj) {
    latestConversationsListRequest += 1;
    const requestId = latestConversationsListRequest;
    commit(types.default.TOGGLE_CONVERSATIONS_LIST_LOADING, true);
    try {
      const response = await Report.getConversationsList({
        metric: reportObj.metric || 'conversations_count',
        from: reportObj.from,
        to: reportObj.to,
        type: reportObj.type,
        id: reportObj.id,
        page: reportObj.page || 1,
        groupBy: reportObj.groupBy,
        businessHours: reportObj.businessHours,
        conversationDirection: reportObj.conversationDirection,
        comunicanteCampaign: reportObj.comunicanteCampaign,
      });

      if (requestId === latestConversationsListRequest) {
        commit(types.default.SET_CONVERSATIONS_LIST, {
          conversations: response.data,
          page: reportObj.page || 1,
          totalCount: Number(response.headers?.['x-total-count'] || 0),
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === latestConversationsListRequest) {
        commit(types.default.TOGGLE_CONVERSATIONS_LIST_LOADING, false);
      }
    }
  },
  fetchAgentsInboxesReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENTS_INBOXES_REPORT_LOADING, true);

    return Report.getAgentsInboxes(reportObj)
      .then(response => {
        commit(types.default.SET_AGENTS_INBOXES_REPORT, response.data);
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      })
      .finally(() => {
        commit(types.default.TOGGLE_AGENTS_INBOXES_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  async fetchAccountSummary({ commit }, reportObj) {
    latestSummaryRequest.account += 1;
    const requestId = latestSummaryRequest.account;
    commit(types.default.TOGGLE_REPORT_SUMMARY_LOADING, {
      summary: 'account',
      value: true,
    });
    try {
      const accountSummary = await Report.getSummary(
        reportObj.from,
        reportObj.to,
        reportObj.type,
        reportObj.id,
        reportObj.groupBy,
        reportObj.businessHours,
        reportObj.conversationDirection,
        reportObj.comunicanteCampaign
      );
      if (requestId === latestSummaryRequest.account) {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      commit(types.default.TOGGLE_REPORT_SUMMARY_LOADING, {
        summary: 'account',
        value: false,
      });
    }
  },
  async fetchBotSummary({ commit }, reportObj) {
    latestSummaryRequest.bot += 1;
    const requestId = latestSummaryRequest.bot;
    commit(types.default.TOGGLE_REPORT_SUMMARY_LOADING, {
      summary: 'bot',
      value: true,
    });
    try {
      const botSummary = await Report.getBotSummary({
        from: reportObj.from,
        to: reportObj.to,
        groupBy: reportObj.groupBy,
        businessHours: reportObj.businessHours,
        conversationDirection: reportObj.conversationDirection,
      });
      if (requestId === latestSummaryRequest.bot) {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      commit(types.default.TOGGLE_REPORT_SUMMARY_LOADING, {
        summary: 'bot',
        value: false,
      });
    }
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    return Report.getConversationMetric(
      reportObj.type,
      reportObj.page,
      reportObj.agentId
    )
      .then(response => {
        commit(types.default.SET_AGENT_CONVERSATION_METRIC, response.data);
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC_TOTAL,
          Number(response.headers['x-total-count']) || 0
        );
      })
      .catch(() => {
        commit(types.default.SET_AGENT_CONVERSATION_METRIC, []);
        commit(types.default.SET_AGENT_CONVERSATION_METRIC_TOTAL, 0);
      })
      .finally(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return downloadReport(reportObj, 'agent', params =>
      Report.getAgentReports(params)
    );
  },
  downloadLabelReports(_, reportObj) {
    return downloadReport(reportObj, 'label', params =>
      Report.getLabelReports(params)
    );
  },
  downloadInboxReports(_, reportObj) {
    return downloadReport(reportObj, 'inbox', params =>
      Report.getInboxReports(params)
    );
  },
  downloadAgentsInboxesReports(_, reportObj) {
    return downloadReport(reportObj, 'agents_inboxes', params =>
      Report.getAgentsInboxesCSV(params)
    );
  },
  downloadTeamReports(_, reportObj) {
    return downloadReport(reportObj, 'team', params =>
      Report.getTeamReports(params)
    );
  },
  downloadConversationsListReports(_, reportObj) {
    return downloadReport(reportObj, 'conversations_list', params =>
      Report.getConversationsListCSV(params)
    );
  },
  downloadConversationsListXlsx(_, reportObj) {
    const { fileName, ...params } = reportObj;

    return fetchAllConversationsList(params).then(conversations => {
      return downloadXlsxFile(fileName, [
        {
          name: 'Conversas',
          rows: conversationRowsForExport(conversations),
        },
      ]);
    });
  },
  downloadEntityReportXlsx(_, reportObj) {
    return Report.getEntityReportXLSX(reportObj.type, reportObj).then(
      response => {
        downloadBinaryFile(
          reportObj.fileName,
          response.data,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
      }
    );
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV({
      onlyAssigned: reportObj.onlyAssigned,
      comunicanteCampaign: reportObj.comunicanteCampaign,
    })
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
          conversationDirection: 'all',
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.SET_CONVERSATIONS_LIST](
    _state,
    { conversations, page, totalCount }
  ) {
    _state.conversationsList.conversations = conversations;
    _state.conversationsList.currentPage = page;
    _state.conversationsList.totalCount = totalCount;
  },
  [types.default.TOGGLE_CONVERSATIONS_LIST_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingConversationsList = flag;
  },
  [types.default.SET_AGENTS_INBOXES_REPORT](_state, report) {
    _state.agentsInboxesReport.data = report;
  },
  [types.default.TOGGLE_AGENTS_INBOXES_REPORT_LOADING](_state, flag) {
    _state.agentsInboxesReport.uiFlags.isFetching = flag;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.TOGGLE_REPORT_SUMMARY_LOADING](_state, { summary, value }) {
    const flag =
      summary === 'bot' ? 'isFetchingBotSummary' : 'isFetchingAccountSummary';
    const pendingRequests = _state.summaryPendingRequests[summary];
    _state.summaryPendingRequests[summary] = value
      ? pendingRequests + 1
      : Math.max(pendingRequests - 1, 0);
    _state.summaryUIFlags[flag] = _state.summaryPendingRequests[summary] > 0;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC_TOTAL](_state, total) {
    _state.overview.agentConversationMetricTotal = total;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
